import { useSuspenseQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CopyTextButton, LifecycleStatus } from '@eluve/blocks';
import {
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  Label,
  SimpleTooltip,
  SortableColumnHeader,
  Stack,
  Switch,
  Text,
  makeDefaultSortableColumnDef,
  makeVisibilityToggleColumnDef,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { LifecycleStatusCell } from '@eluve/smart-blocks';

import { AddUserAction } from './components/AddUserAction';
import { ImpersonateUserButton } from './components/ImpersonateUserButton';
import { RoleSelector } from './components/RoleSelector';
import { UserExcludeButton } from './components/UserExcludeButton';
import { UserRemoveButton } from './components/UserRemoveButton';
import { TenantUserFragment } from './operations';

export type TenantUserRow = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  role: string;
  lastAccessedAt: string;
  tenantId: string;
  isExcluded: boolean;
  lifecycleStatus: LifecycleStatus;
};

const GetTenantUsersDocument = graphql(
  `
    query GetTenantUsers(
      $tenantId: uuid!
      $tenantUsersWhere: TenantUsersBoolExp
    ) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        tenantUsers(where: $tenantUsersWhere) {
          ...TenantUser
        }
      }
    }
  `,
  [TenantUserFragment],
);

export const AdminTenantUsers: React.FC = () => {
  const { tenantId } = useParams() as { tenantId: string };
  const [showExcluded, setShowExcluded] = useState(false);
  const { data, refetch } = useSuspenseQuery(GetTenantUsersDocument, {
    variables: {
      tenantId: tenantId,
      tenantUsersWhere: showExcluded ? {} : { isExcluded: { _eq: false } },
    },
  });

  const columns = useMemo(
    () =>
      new ColDefBuilder<TenantUserRow>()
        .colDef(
          makeVisibilityToggleColumnDef({
            ...makeDefaultSortableColumnDef('id', 'Id'),
            id: 'id',
            accessorFn: (row) => row.id,
            hiddenCell: ({ row }) => (
              <CopyTextButton copyText={row.original.id}>
                <Icon name="User" size="xl" />
              </CopyTextButton>
            ),
          }),
        )
        .defaultSortable('firstName', 'First')
        .defaultSortable('lastName', 'Last')
        .defaultSortable('email')
        .dateSortable('createdAt', 'Created At')
        .dateSortable('lastAccessedAt', 'Last Signed In')
        .colDef({
          accessorKey: 'role',
          header: ({ column }) => (
            <SortableColumnHeader column={column} label="Role" />
          ),
          cell: ({ row }) => {
            return (
              <RoleSelector
                tenantId={row.original.tenantId}
                userId={row.original.id}
              />
            );
          },
        })
        .colDef({
          id: 'lifecycleStatus',
          header: 'Status',
          cell: ({ row }) => (
            <LifecycleStatusCell
              tenantId={row.original.tenantId}
              userId={row.original.id}
              status={row.original.lifecycleStatus}
              isExcluded={row.original.isExcluded}
              subjectLabel={row.original.email}
              onChanged={() => refetch()}
            />
          ),
        })
        .colDef({
          accessorKey: 'action',
          header: () => (
            <Text size="xs" weight="semibold">
              Actions
            </Text>
          ),
          cell: ({ row }) => {
            return (
              <Stack gap={2} direction="horizontal">
                <ImpersonateUserButton
                  userId={row.original.id}
                  tenantId={row.original.tenantId}
                  userEmail={row.original.email}
                  userName={`${row.original.firstName} ${row.original.lastName}`.trim()}
                />
                <UserExcludeButton
                  userId={row.original.id}
                  tenantId={row.original.tenantId}
                  isExcluded={row.original.isExcluded}
                />
                <UserRemoveButton
                  user={row.original}
                  tenantId={row.original.tenantId}
                />
              </Stack>
            );
          },
        })
        .build(),
    [refetch],
  );

  const rows = (data?.tenantsByPk?.tenantUsers ?? []).map<TenantUserRow>(
    (t) => {
      return {
        id: t.userId,
        firstName: t.user?.firstName ?? '',
        lastName: t.user?.lastName ?? '',
        email: t.user?.email ?? '',
        createdAt: t.createdAt,
        role: t.role ?? '',
        lastAccessedAt: t.lastAccessedAt,
        tenantId: t.tenantId,
        isExcluded: t.isExcluded,
        lifecycleStatus: t.lifecycleStatus,
      };
    },
  );

  return (
    <Stack gap={2}>
      <Stack gap={2} direction="horizontal" justify="between">
        <div className="my-2 py-2">
          <Heading>Users</Heading>
        </div>
        <AddUserAction tenantId={tenantId} onUserAdded={() => refetch()} />
      </Stack>
      <Stack direction="horizontal" gap={2} align="center" justify="end">
        <Switch
          id="show-excluded-users"
          checked={showExcluded}
          onCheckedChange={setShowExcluded}
          aria-label="Show excluded users"
        />
        <Label htmlFor="show-excluded-users" className="cursor-pointer">
          Show excluded users
        </Label>
        <SimpleTooltip label="Excluded users don't count toward usage reports">
          <span className="inline-flex">
            <Icon name="Info" size="sm" />
          </span>
        </SimpleTooltip>
      </Stack>
      <DataTable data={rows} columns={columns} enableGlobalSearch />
    </Stack>
  );
};
